import React, { useState, useEffect, useRef } from 'react';
import parse from 'html-react-parser';
import { useHistory, useLocation } from 'react-router-dom';
import ReactDevicePreview from 'react-device-preview';
import {
  Container, Row, Col, Card, CardBody, Button
} from 'reactstrap';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from '../../components/draftjs-to-html';
import Loader from '../../shared/loader/loader';
import { ShowNotification } from '../../helpers/general/generalHelpers';
import MobileScreen from './MobileScreen';
import AppendixModal from './AppendixModal';
import ShowImageModal from './ShowImageModal';
import { addNewCaseStudy, getCaseStudy, updateCaseStudy } from './services/CaseStudies';
import { UploadMarkingImage } from '../../helpers/general/services/image-service';

// Import the new Custom Quill Editor
import CustomQuillEditor from '../../components/CustomQuillEditor/CustomQuillEditor';

// Initialize Buffer for file handling
window.Buffer = window.Buffer || require("buffer").Buffer;

const AddEditCaseStudy = ({ location: routerLocation }) => {
  const location = useLocation();
  const { type, subject, paperNumber } = location.state || {};
  const history = useHistory();
  const dropzoneElement = useRef(null);
  const quillEditorRef = useRef(null); // Ref for the Quill editor

  // State management
  const [loader, setLoader] = useState(true);
  const [caseStudyData, setCaseStudyData] = useState({
    internalId: '',
    studyLevelId: 'O Level',
    subject: {}, // Initialize subject as an empty object
    year: new Date().getFullYear(),
    session: 'May/June',
    paperNumber: 2,
    versionNumber: 'V1',
    markingSchemeId: '-',
    caseStudy: '',
    appendix: []
  });
  const [caseStudyContent, setCaseStudyContent] = useState(''); // State for editor content as HTML string
  const [appendix, setAppendix] = useState([]);
  const [selectedImage, setSelectedImage] = useState('');
  const [isVisibleImageModal, setIsVisibleImageModal] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);

  // Load data based on type (add/edit)
  useEffect(() => {
    if (type === "add") {
      setCaseStudyData(prev => ({
        ...prev,
        internalId: 'new-case-study', // Provide a key for new items
        studyLevelId: subject?.studyLevelId || 'O Level',
        subject: subject,
        paperNumber: paperNumber?.value || 2,
      }));
      setLoader(false);
    } else if (routerLocation.search) {
      const questionId = new URLSearchParams(routerLocation.search).get('id');

      getCaseStudy(questionId)
          .then((response) => {
            setCaseStudyData(response.data);
            setCaseStudyContent(response.data.caseStudy || ''); // Set HTML content directly
            setAppendix(response.data.appendix.map(url => ({ url, preview: url })));
          })
          .catch((error) => {
            ShowNotification({ type: 'error', message: error.message });
            history.goBack();
          })
          .finally(() => setLoader(false));
    } else {
      history.goBack();
    }
  }, [type, subject, paperNumber, routerLocation.search, history]);

  // Handle file uploads
  const handleAcceptedFiles = (files) => {
    if (files.length) {
      const file = files[0];
      UploadMarkingImage(file, 'caseStudyAppendix')
          .then((result) => {
            setAppendix(prev => [...prev, { url: result.location, preview: result.location }]);
            ShowNotification({ type: 'success', message: 'Image uploaded successfully' });
            setIsVisibleModal(false);
          })
          .catch(err => {
            ShowNotification({ type: 'error', message: 'Image upload failed' });
          });
    }
  };

  // Remove appendix image
  const removeAppendix = (index) => {
    setAppendix(prev => prev.filter((_, i) => i !== index));
  };

  // Create new case study
  const handleAddCaseStudy = async () => {
    const payload = {
      ...caseStudyData,
      caseStudy: caseStudyContent, // Use HTML content from state
      appendix: appendix.map(item => item.url || item.preview).filter(url => url)
    };

    addNewCaseStudy(payload, history)
        .then(() => ShowNotification({ type: 'success', message: 'Case study added successfully' }))
        .catch(error => ShowNotification({ type: 'error', message: error.message }));
  };

  // Update existing case study
  const handleUpdateCaseStudy = async () => {
    const questionId = new URLSearchParams(routerLocation.search).get('id');
    const payload = {
      ...caseStudyData,
      caseStudy: caseStudyContent, // Use HTML content from state
      appendix: appendix.map(item => item.url || item.preview).filter(url => url)
    };

    updateCaseStudy(questionId, payload)
        .then(() => ShowNotification({ type: 'success', message: 'Case study updated successfully' }))
        .catch(error => ShowNotification({ type: 'error', message: error.message }));
  };

  return (
      <React.Fragment>
        <Loader loading={loader} />
        <div className="page-content">
          <Container fluid className="relative">
            <Row>
              <Col xs="12">
                <div onClick={() => history.goBack()} className="page-title-box d-flex align-items-center justify-content-between">
                <span className="mb-0 font-size-14 back-button">
                  <i className="fa fa-arrow-left"></i> Back to Case Studies
                </span>
                </div>
              </Col>
            </Row>

            <Card>
              <CardBody>
                <Row>
                  <Col md="2" className="subject-creator-info-section font-size-13">
                    <div className="subject-creator-info">
                      <p>Study level: {caseStudyData.studyLevelId || '-'}</p>
                      <p>Year: {caseStudyData.year || '-'}</p>
                    </div>
                  </Col>
                  <Col md="2" className="subject-creator-info-section font-size-13">
                    <div className="subject-creator-info">
                      {/* FIX: Use dynamic data for subject title */}
                      <p>Subject: {caseStudyData.subject?.title || '-'}</p>
                      <p>Session: {caseStudyData.session || '-'}</p>
                    </div>
                  </Col>
                  <Col md="2" className="subject-creator-info-section font-size-13">
                    <div className="subject-creator-info">
                      <p>Paper Number: {caseStudyData.paperNumber || '-'}</p>
                      <p>Version Number: {caseStudyData.versionNumber || '-'}</p>
                    </div>
                  </Col>
                  <Col className="subject-creator-info-section font-size-13">
                    <div className="subject-creator-info">
                      <p>Marking Scheme ID: {caseStudyData.markingSchemeId || '-'}</p>
                      <p>Question ID: {caseStudyData.internalId || '-'}</p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <Row>
                  <Col md="12" className="justify-content-center">
                    <h4 className="card-title mb-3">Case Study Detail</h4>
                  </Col>
                </Row>
                <Row className="section-top">
                  <Col md="6" className="text-center section-right">
                    <ReactDevicePreview device="iphone8" scale="0.7">
                      <div className="device-content">
                        {/* FIX: Use dynamic data for mobile screen header */}
                        <MobileScreen cardHeaderText={caseStudyData.subject?.title || 'Case Study'}>
                          <h2 className="card-title mb-1 mt-3">Case Study</h2>
                          {parse(caseStudyContent || '')}

                          {appendix.length > 0 && (
                              <>
                                <h2 className="card-title mb-1 mt-3">Appendix Images</h2>
                                <div className="appendix-images-preview">
                                  {appendix.map((item, index) => (
                                      <img
                                          key={index}
                                          src={item.preview}
                                          alt={`Appendix ${index + 1}`}
                                          className="img-thumbnail m-1"
                                          style={{ maxWidth: '100px', maxHeight: '100px' }}
                                      />
                                  ))}
                                </div>
                              </>
                          )}
                        </MobileScreen>
                      </div>
                    </ReactDevicePreview>
                  </Col>
                  <Col md="6">
                    <div className="mt-0 p-3 rounded bg-white">
                      <div className="card border-0">
                        <div className="mb-3">
                          <label className="form-label">Case Study Content</label>
                          {!loader && (
                              <CustomQuillEditor
                                  key={caseStudyData.internalId}
                                  ref={quillEditorRef}
                                  initialHtml={caseStudyContent}
                                  onTextChange={() => {
                                    if (quillEditorRef.current) {
                                      const newHtml = quillEditorRef.current.root.innerHTML;
                                      if (caseStudyContent !== newHtml) {
                                        setCaseStudyContent(newHtml);
                                      }
                                    }
                                  }}
                              />
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Appendix Images</label>
                          <Button
                              onClick={() => setIsVisibleModal(true)}
                              className="btn btn-primary mb-3"
                          >
                            Add Appendix Image
                          </Button>

                          <div className="appendix-images-container">
                            {appendix.map((item, index) => (
                                <div key={index} className="appendix-image-item mb-2">
                                  <img
                                      src={item.preview}
                                      alt={`Appendix ${index + 1}`}
                                      className="img-thumbnail"
                                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                  />
                                  <div className="d-flex justify-content-center mt-1">
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeAppendix(index)}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="12" className="text-right mt-3">
                    <Button
                        color="secondary"
                        onClick={() => history.goBack()}
                        className="mr-2"
                    >
                      Cancel
                    </Button>

                    {type !== "add" ? (
                        <Button color="primary" onClick={handleUpdateCaseStudy}>
                          Update Case Study
                        </Button>
                    ) : (
                        <Button color="primary" onClick={handleAddCaseStudy}>
                          Add Case Study
                        </Button>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Container>
        </div>

        <AppendixModal
            modalOpen={isVisibleModal}
            toggleModal={() => setIsVisibleModal(false)}
            handleAcceptedFiles={handleAcceptedFiles}
            dropzoneElement={dropzoneElement}
        />

        <ShowImageModal
            modalOpen={isVisibleImageModal}
            selectedImage={selectedImage}
            toggleModal={() => setIsVisibleImageModal(false)}
        />
      </React.Fragment>
  );
};

export default AddEditCaseStudy;